import { useCallback, useRef, useState } from 'react';
import { useRAFScroll } from './useRAFScroll';

/** Viewport height units per card for scroll-based transitions */
const SCROLL_HEIGHT_PER_CARD_VH = 75;

interface UseCardsVerticalScrollDesktopOptions {
  cardCount: number;
  onActiveCardChange?: () => void;
}

type ScrollPosition = 'start' | 'middle' | 'end';

interface UseCardsVerticalScrollDesktopReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  activeCardIndex: number;
  scrollHeight: string;
  scrollToCard: (cardIndex: number, position?: ScrollPosition) => void;
}

/**
 * Hook to manage scroll-based active card detection for the desktop CardsVerticalScroll component.
 * Tracks scroll position to determine which card should be active based on viewport position.
 */
export const useCardsVerticalScrollDesktop = ({
  cardCount,
  onActiveCardChange,
}: UseCardsVerticalScrollDesktopOptions): UseCardsVerticalScrollDesktopReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Track scroll position to determine active card
  const updateActiveCard = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const containerTop = containerRect.top + scrollY;
    const viewportHeight = window.innerHeight;

    // Calculate which card should be active based on scroll position
    // Each card gets an equal portion of the scroll range
    const scrollIntoContainer = scrollY - containerTop + viewportHeight * 0.5;
    const scrollRangePerCard = (containerRect.height - viewportHeight) / cardCount;

    let newActiveIndex = Math.floor(scrollIntoContainer / scrollRangePerCard);
    newActiveIndex = Math.max(0, Math.min(cardCount - 1, newActiveIndex));

    setActiveCardIndex(prevIndex => {
      if (newActiveIndex !== prevIndex) {
        onActiveCardChange?.();
      }
      return newActiveIndex;
    });
  }, [cardCount, onActiveCardChange]);

  useRAFScroll({
    onScroll: updateActiveCard,
    enabled: cardCount > 0,
    listenToResize: true,
  });

  // Calculate scroll height based on number of cards
  const scrollHeight = `${cardCount * SCROLL_HEIGHT_PER_CARD_VH + 100}vh`;

  // Scroll to a specific card with smooth animation
  // position determines where within the card's scroll zone to position:
  // - 'start': beginning of card's zone (enables immediate page scroll up)
  // - 'middle': center of card's zone (default)
  // - 'end': end of card's zone (enables immediate page scroll down)
  const scrollToCard = useCallback(
    (cardIndex: number, position: ScrollPosition = 'middle') => {
      const container = containerRef.current;
      if (!container) return;

      const clampedIndex = Math.max(0, Math.min(cardCount - 1, cardIndex));
      const scrollY = window.scrollY || window.pageYOffset;
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top + scrollY;
      const viewportHeight = window.innerHeight;
      const scrollRangePerCard = (containerRect.height - viewportHeight) / cardCount;
      const lastIndex = cardCount - 1;

      let targetScrollY: number;

      // Handle boundary cases to enable immediate page scroll
      if (clampedIndex === 0 && position === 'start') {
        // First card at 'start': position at very beginning so next ArrowUp scrolls page
        targetScrollY = containerTop;
      } else if (clampedIndex === lastIndex && position === 'end') {
        // Last card at 'end': position at very end so next ArrowDown scrolls page
        targetScrollY = containerTop + containerRect.height - viewportHeight;
      } else {
        // Standard positioning within card's scroll zone
        const positionMultipliers: Record<ScrollPosition, number> = {
          start: 0,
          middle: 0.5,
          end: 1,
        };
        const positionMultiplier = positionMultipliers[position];

        // Calculate target scroll position (inverse of the active card calculation)
        targetScrollY =
          containerTop +
          clampedIndex * scrollRangePerCard -
          viewportHeight * 0.5 +
          scrollRangePerCard * positionMultiplier;
      }

      window.scrollTo({
        top: targetScrollY,
        behavior: 'instant',
      });
    },
    [cardCount],
  );

  return {
    containerRef,
    activeCardIndex,
    scrollHeight,
    scrollToCard,
  };
};
